import { Intent } from "@blueprintjs/core/lib/esm/common/intent";
import { ButtonSave } from "components/buttons";
import { FormCheckboxList, FormTextInput } from "components/form-fields";
import { ShowHide } from "components/layout";
import { ListingPagination } from "components/listing";
import { AlertUnsavedData, NotificationToaster } from "components/notifications";
import { TagList } from "components/status";
import { useDebounce } from "hooks/useDebounce";
import { useDirtyData } from "hooks/useDirtyData";
import React, { useMemo, useRef, useEffect, useState } from "react";
import { SelfReportingService } from "services";

export function UserReportSchedules({ userId }) {
    const debounceTimeout = 750;
    const listSize = 50;

    const [loadingPage, setLoadingPage] = useState(true);
    const [loadingSelectedSchedules, setLoadingSelectedSchedules] = useState(true);
    const loading = loadingPage || loadingSelectedSchedules;

    const [saving, setSaving] = useState(false);
    const [schedules, setSchedules] = useState([ // Dummy data to show loading state
        { id: 1, name: "" },
        { id: 2, name: "" },
        { id: 3, name: "" }
    ]);
    const [selectedSchedules, setSelectedSchedules] = useState([]);
    const [originalSelectedSchedules, setOriginalSelectedSchedules] = useState([]);

    const isDataDirty = useDirtyData(originalSelectedSchedules, selectedSchedules);

    const [scheduleOptions, setScheduleOptions] = useState([]);

    const mappedSchedules = useMemo(() => {
        return schedules.map(s => ({
            id: s.id,
            name: s.name,
            checked: selectedSchedules.some(selected => selected.id === s.id)
        }));
    }, [schedules, selectedSchedules]);

    const [searchTerm, setSearchTerm] = useState("");

    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const inputSearchRef = useRef(null);

    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(1)

    useEffect(() => {
        if (userId) {
            fetchUsersSchedules();
        }
    }, [userId]);

    useEffect(() => {
        fetchOptions();
    }, []);

    useEffect(() => {
        fetchAllSchedules();
    }, [debouncedSearchTerm, pageNumber, scheduleOptions]);

    function fetchUsersSchedules() {
        setLoadingSelectedSchedules(true);

        const userNotSyncedResponseStatus = 403;

        return SelfReportingService.getUsersSchedules(userId).then(schedulesResponse => {
            const newSelected = schedulesResponse.map(s => ({ id: s.scheduleId, name: s.scheduleName }));
            setOriginalSelectedSchedules(newSelected);
            setSelectedSchedules([...newSelected]);
            setLoadingSelectedSchedules(false)
        }, (error) => {
            if (error.response && error.response.status === userNotSyncedResponseStatus) {
                setSelectedSchedules([]);
                setOriginalSelectedSchedules([]);
                setLoadingSelectedSchedules(false);
            } else {
                NotificationToaster.show(Intent.DANGER, "Unable to load the user's schedules, please try again.");
            }
        });
    }

    function fetchAllSchedules() {
        if (!scheduleOptions.length) {
            return;
        }

        const filter = scheduleOptions.filter(o => o.key !== 'COMPLETE').map(o => ({ name: 'Status', value: o.name })); // Exclude COMPLETE schedules
        setLoadingPage(true);

        SelfReportingService.getSchedulesList(listSize, pageNumber, debouncedSearchTerm, "", "", filter).then(function (u) {
            setTotalPages(Math.ceil(u.totalCount / listSize));
            setLoadingPage(false);
            setSchedules(u.data);
        }, () => {
            NotificationToaster.show(Intent.DANGER, "Unable to load the schedules, please try again.");
        });
    }

    function fetchOptions() {
        return SelfReportingService.getScheduleStatuses().then((options) => {
            setScheduleOptions(options);
        }, () => {
            NotificationToaster.show(Intent.DANGER, "Unable to load the schedules, please try again.");
        });
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function onItemChange(event, id) {
        var isChecked = !!event?.target?.checked;

        var selectedSchedulesCopy = [...selectedSchedules];
        const schedule = schedules.find(s => s.id === id);

        if (isChecked) {
            selectedSchedulesCopy.push({ id: id, name: schedule.name});
        }
        else {
            selectedSchedulesCopy = selectedSchedulesCopy.filter(s => s.id !== id);
        }

        setSelectedSchedules(selectedSchedulesCopy);
    }

    function onPageChange(newPage) {
        setPageNumber(newPage);
    }

    function saveSchedules() {
        setSaving(true);

        const newEnabledScheduleIds = selectedSchedules.map(s => s.id)
            .filter(e => !originalSelectedSchedules.some(os => os.id === e));

        const newDisabledScheduleIds = originalSelectedSchedules.map(s => s.id)
            .filter(os => !selectedSchedules.some(e => e.id === os));

        const request = {
            enabledSchedules: newEnabledScheduleIds,
            disabledSchedules: newDisabledScheduleIds
        }

        return SelfReportingService.saveUsersSchedules(userId, request).then(() => {
            NotificationToaster.show(Intent.SUCCESS, "Schedules saved successfully.");
            fetchUsersSchedules();
            setSaving(false);
        }, () => {
            setSaving(false);
            NotificationToaster.show(Intent.DANGER, "Unable to save the schedules, please try again.");
        })
    }

    function sortSelectedSchedules() {
        const sortedSchedules = [...selectedSchedules]
        sortedSchedules.sort((a, b) => a.name.localeCompare(b.name));
        return sortedSchedules;
    }

    return (
        <div className="row">
            <h2>Report Schedules</h2>
            <div className="intro-text">Manage the report schedules assigned to this user. Completed schedules are not shown, as they can no longer be edited.</div>
            <TagList items={sortSelectedSchedules()} disabled={saving} onRemoveTag={onItemChange} />

            <div className="inline-items spacer-bottom">
                <FormTextInput loading={loadingSelectedSchedules} inputRef={inputSearchRef} placeholder="Schedule Search" onChange={onSearchChange} value={searchTerm} large disabled={saving} icon="search" id="user-schedule-search-field" />
            </div>

            <FormCheckboxList
                id="schedule-list"
                items={mappedSchedules}
                onChange={onItemChange}
                loading={loading}
                dangerHelperText=""
                disabled={saving}
                striped={true}
                showTags={false}
            ></FormCheckboxList>

            <ShowHide
                evaluator={totalPages > 1}
                show={(
                    <ListingPagination currentPage={pageNumber} totalPages={totalPages} loadingData={false} onPageChange={onPageChange} />
                )}
            />
            
            <AlertUnsavedData
                isDirty={isDataDirty}>
            </AlertUnsavedData>

            <ButtonSave
                id="user-report-schedules-save"
                onClick={saveSchedules}
                disabled={saving}
                loading={loading}
            ></ButtonSave>
        </div>
    )
}